/**
 * Date Range Calculator
 * TASK-293: Single responsibility - Calculate date range for meeting prep searches
 *
 * Logic:
 * 1. Fetch last COMPLETED meeting for this client
 * 2. If last meeting exists: Search from last_meeting.ended_at to NOW
 * 3. If NO previous meeting: Search from (meeting_date - 3 months) to NOW
 */

import { apiService } from '../../services/api/apiService';
import type { DateRangeInfo, LastMeetingInfo } from './types';

/**
 * Interface for meeting service dependency (for testability)
 */
export interface IMeetingService {
  getLastMeetingForClient(clientId: string): Promise<{
    client_id: string;
    has_previous_meeting: boolean;
    last_meeting: LastMeetingInfo | null;
  }>;
}

/**
 * Default implementation using apiService
 */
const defaultMeetingService: IMeetingService = {
  getLastMeetingForClient: (clientId: string) => apiService.getLastMeetingForClient(clientId),
};

/**
 * Calculate the date range for meeting prep searches
 *
 * @param clientId - The client ID to fetch last meeting for
 * @param meetingDate - The scheduled date of the upcoming meeting
 * @param meetingService - Optional meeting service for dependency injection
 * @returns DateRangeInfo with calculated date range
 */
export async function calculateDateRange(
  clientId: string,
  meetingDate: Date,
  meetingService: IMeetingService = defaultMeetingService
): Promise<DateRangeInfo> {
  try {
    const response = await meetingService.getLastMeetingForClient(clientId);

    // If there's a previous completed meeting with an ended_at date
    if (response.has_previous_meeting && response.last_meeting?.ended_at) {
      return {
        dateFrom: response.last_meeting.ended_at,
        dateTo: new Date().toISOString(),
        lastMeeting: response.last_meeting,
        isFirstMeeting: false,
      };
    }

    // No previous meeting - use 3 months before meeting date
    const threeMonthsAgo = new Date(meetingDate);
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

    return {
      dateFrom: threeMonthsAgo.toISOString(),
      dateTo: new Date().toISOString(),
      lastMeeting: null,
      isFirstMeeting: true,
    };
  } catch (error) {
    // On error, fall back to 3 months before meeting date
    console.error('[DateRangeCalculator] Failed to fetch last meeting:', error);

    const threeMonthsAgo = new Date(meetingDate);
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

    return {
      dateFrom: threeMonthsAgo.toISOString(),
      dateTo: new Date().toISOString(),
      lastMeeting: null,
      isFirstMeeting: true,
    };
  }
}

/**
 * Format date range for display
 */
export function formatDateRange(dateRange: DateRangeInfo): string {
  const from = new Date(dateRange.dateFrom);
  const to = new Date(dateRange.dateTo);

  const formatOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };

  const fromStr = from.toLocaleDateString('en-US', formatOptions);
  const toStr = to.toLocaleDateString('en-US', formatOptions);

  if (dateRange.isFirstMeeting) {
    return `Last 3 months (${fromStr} - ${toStr})`;
  }

  return `Since last meeting (${fromStr} - ${toStr})`;
}

/**
 * Get the number of days in the date range
 */
export function getDateRangeDays(dateRange: DateRangeInfo): number {
  const from = new Date(dateRange.dateFrom);
  const to = new Date(dateRange.dateTo);
  const diffMs = to.getTime() - from.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}
